
import { computed, defineComponent, getCurrentInstance, onBeforeUnmount, onMounted, PropType, ref, watch } from 'vue'
import qs from 'query-string'
import config from '@root/config'
import { getDetailedIntegrationData } from '../../../api/marketplace'
import { useProject } from '../../../hooks/useProject'
import {
    DetailedIntegrationData,
    MarketplaceIntegration,
    MarketplaceIntegrationFeedback,
} from '../../../types/marketplace'
import IntegrationImagePreview from './IntegrationImagePreview.vue'
import IntegrationChannelsOverview from './IntegrationChannelsOverview.vue'
import BusinessPulseReportPreview from './BusinessPulseReportPreview.vue'
import Opz2ReportPreview from './Opz2ReportPreview.vue'
import HotWindowsPreview from './HotWindowsPreview.vue'
import ZvonoBotPreview from './ZvonoBotPreview.vue'
import UxIcon from './UxIcon.vue'
import { useIntegrationPageUx } from '@/helpers/marketplace/useIntegrationPageUx'
import { getInstallCountNoun } from '@/helpers/marketplace/integrationPurchaseCount'

const FEATURE_ICONS = ['⚙️', '📈', '🛠️', '🎯', '🚀', '💬', '🛡️', '⏰', '📊', '📡', '🔥']

export default defineComponent({
    components: { IntegrationImagePreview, IntegrationChannelsOverview, BusinessPulseReportPreview, Opz2ReportPreview, HotWindowsPreview, UxIcon, ZvonoBotPreview },
    props: {
        integration: {
            type: Object as PropType<MarketplaceIntegration>,
            required: true,
        },
        integrationFeedback: {
            type: Array as PropType<MarketplaceIntegrationFeedback[]>,
            required: true,
        },
    },
    setup(props) {
        const { project } = useProject()
        const integrationRef = computed(() => props.integration)
        const {
            aboutDescriptions,
            aboutFacts,
            useUxAboutFacts,
            skillsCards,
            skillsSectionSubtitle,
        } = useIntegrationPageUx(integrationRef)
        const root = getCurrentInstance()?.proxy as { $root?: { $on: Function; $off: Function } } | undefined

        const overviewIntegrationIds = [
            config.marketplaceModulesIds[config.buildType].maxBusiness,
            config.marketplaceModulesIds[config.buildType].channels,
            config.marketplaceModulesIds[config.buildType].sms,
        ]

        const showOverviewBlock = computed(() => overviewIntegrationIds.includes(props.integration.id))
        const isBusinessPulseReport = computed(
            () =>
                String(props.integration.id) ===
                String(config.marketplaceModulesIds[config.buildType].businessPulseReport),
        )
        const isOpz2 = computed(
            () => String(props.integration.id) === String(config.marketplaceModulesIds[config.buildType].opz2),
        )
        const isHotWindows = computed(
            () => String(props.integration.id) === String(config.marketplaceModulesIds[config.buildType].hotWindows),
        )
        const isZvonoBot = computed(
            () => String(props.integration.id) === String(config.marketplaceModulesIds[config.buildType].zvonoBot),
        )

        const overviewLoading = ref(false)
        const overviewError = ref('')
        const overviewData = ref<DetailedIntegrationData | null>(null)

        const overviewSlots = computed(() => overviewData.value?.detailInfo ?? [])

        const fetchOverviewData = async ({ preserveData = false }: { preserveData?: boolean } = {}) => {
            if (!showOverviewBlock.value) return

            const projectId = project.value?.id
            if (!projectId) {
                overviewData.value = null
                overviewError.value = 'Выберите проект для загрузки обзора'
                return
            }

            overviewLoading.value = true
            overviewError.value = ''
            if (!preserveData) {
                overviewData.value = null
            }

            try {
                const { data, error } = await getDetailedIntegrationData(projectId, props.integration.id)
                if (error) {
                    overviewError.value = 'Не удалось загрузить данные обзора'
                    return
                }
                overviewData.value = data
            } catch {
                overviewError.value = 'Не удалось загрузить данные обзора'
            } finally {
                overviewLoading.value = false
            }
        }

        const handleOverviewRefresh = () => {
            void fetchOverviewData({ preserveData: true })
        }

        const handleReloadChannelSlots = () => {
            void fetchOverviewData({ preserveData: true })
        }

        watch(
            () => [props.integration.id, project.value?.id] as const,
            () => {
                void fetchOverviewData()
            },
            { immediate: true }
        )

        onMounted(() => {
            root?.$root?.$on('reload-channel-slots', handleReloadChannelSlots)
        })

        onBeforeUnmount(() => {
            root?.$root?.$off('reload-channel-slots', handleReloadChannelSlots)
        })

        const hasRating = computed(() => Number(props.integration.feedbackCount || 0) > 0)
        const ratingValue = computed(() => Number(props.integration.feedbackRating || 0))

        const ratingLabel = computed(() => (hasRating.value ? ratingValue.value.toFixed(1) : '—'))
        const feedbackCountLabel = computed(() =>
            Number(props.integration.feedbackCount || 0).toLocaleString('ru-RU')
        )
        const installsLabel = computed(() =>
            Number(props.integration.installCount || 0).toLocaleString('ru-RU')
        )
        const installCountUnit = computed(() => getInstallCountNoun(props.integration.id))
        const trialDaysLabel = computed(() => {
            const days = Number(props.integration.trialDays || 0)
            return days > 0 ? String(days) : ''
        })

        const featureCards = computed(() => {
            if (skillsCards.value.length) {
                return skillsCards.value
            }

            const possibilities = props.integration.descriptionPossibilities || []
            if (!possibilities.length) {
                return [
                    { icon: '⚙️', title: 'Быстрый запуск', description: 'Подключение и настройка в несколько шагов.' },
                    { icon: '📈', title: 'Рост показателей', description: 'Измеримый эффект в ключевых метриках проекта.' },
                    { icon: '🛠️', title: 'Гибкая настройка', description: 'Адаптация под процессы вашей команды.' },
                ]
            }
            return possibilities.map((entry, idx) => ({
                icon: FEATURE_ICONS[idx % FEATURE_ICONS.length],
                title: entry.text ? `Возможность ${idx + 1}` : 'Возможность',
                description: entry.text || 'Описание будет добавлено позже.',
            }))
        })

        const srcIsYoutube = (src: string) =>
            /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)/i.test(src || '')

        const getYoutubeVideoId = (url: string) => {
            const parsed = qs.parseUrl(url)
            if (parsed.url.includes('youtu.be')) {
                return parsed.url.split('/').filter(Boolean).pop() || ''
            }
            const id = parsed.query.v
            return typeof id === 'string' ? id : ''
        }

        const getYoutubeThumbnail = (url: string) => {
            const videoId = getYoutubeVideoId(url)
            return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : ''
        }

        const galleryItems = computed(
            () =>
                props.integration.descriptionMedia?.map(el => {
                    const type =
                        el.type === 'youtube' || srcIsYoutube(el.src) ? 'youtube' : 'image'
                    return {
                        ...el,
                        type,
                        previewSrc: type === 'youtube' ? getYoutubeThumbnail(el.src) : el.src,
                    }
                }) ?? []
        )

        const imagePreviewOpened = ref(false)
        const imagePreviewIndex = ref(0)

        const imagePreviewSlides = computed(() =>
            galleryItems.value
                .map((media, index) => ({ media, index }))
                .filter(({ media }) => media.type === 'image')
                .map(({ media, index }) => ({
                    src: media.src,
                    title: `Скриншот ${index + 1}`,
                }))
        )

        const onGalleryClick = (media: { type: string; src: string }, index: number) => {
            if (media.type === 'youtube') {
                window.open(media.src, '_blank', 'noopener,noreferrer')
                return
            }
            const slideIndex = imagePreviewSlides.value.findIndex(slide => slide.src === media.src)
            imagePreviewIndex.value = slideIndex >= 0 ? slideIndex : 0
            imagePreviewOpened.value = true
        }

        return {
            project,
            showOverviewBlock,
            overviewLoading,
            overviewError,
            overviewSlots,
            handleOverviewRefresh,
            ratingLabel,
            feedbackCountLabel,
            installsLabel,
            installCountUnit,
            trialDaysLabel,
            aboutDescriptions,
            aboutFacts,
            useUxAboutFacts,
            skillsSectionSubtitle,
            featureCards,
            isBusinessPulseReport,
            isOpz2,
            isHotWindows,
            isZvonoBot,
            galleryItems,
            imagePreviewOpened,
            imagePreviewSlides,
            imagePreviewIndex,
            onGalleryClick,
        }
    },
})
